import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiLoader, 
  FiList,
  FiArrowUp,
  FiArrowDown,
  FiSave,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiSearch
} from "react-icons/fi";

const StepsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState([]);
  const [filteredSteps, setFilteredSteps] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  
  // Form states
  const [newSteps, setNewSteps] = useState([{ text: "", description: "" }]);
  const [editingSteps, setEditingSteps] = useState({ id: null, steps: [] });
  
  // Loading states
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const API_URL = "https://api.dreamtoday.in/api/admin";

  // Fetch all steps
  const fetchSteps = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/getallsteps`, {
        withCredentials: true
      });
      
      console.log("Steps response:", response.data);
      
      if (response.data.success) {
        setSteps(response.data.steps || []);
        setFilteredSteps(response.data.steps || []);
      }
    } catch (error) {
      console.error("Error fetching steps:", error);
      toast.error("Failed to fetch steps");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSteps();
  }, []);

  // Search and filter
  useEffect(() => {
    if (searchTerm) {
      const filtered = steps.filter(stepDoc => 
        stepDoc.steps.some(step => 
          step.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (step.description && step.description.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      );
      setFilteredSteps(filtered);
    } else {
      setFilteredSteps(steps);
    }
    setCurrentPage(1);
  }, [searchTerm, steps]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSteps.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSteps.length / itemsPerPage);

  // Handle create steps
  const handleCreateSteps = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const validSteps = newSteps.filter(step => step.text.trim() !== "");
      
      if (validSteps.length === 0) {
        toast.error("At least one step is required");
        setCreating(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/createsteps`,
        { steps: validSteps },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Steps created successfully!");
        setShowCreateModal(false);
        setNewSteps([{ text: "", description: "" }]);
        fetchSteps();
      }
    } catch (error) {
      console.error("Error creating steps:", error);
      toast.error(error.response?.data?.message || "Failed to create steps");
    } finally {
      setCreating(false);
    }
  };

  // Handle update steps
  const handleUpdateSteps = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const validSteps = editingSteps.steps.filter(step => step.text.trim() !== "");
      
      if (validSteps.length === 0) {
        toast.error("At least one step is required");
        setUpdating(false);
        return;
      }

      const response = await axios.put(
        `${API_URL}/updatesteps/${editingSteps.id}`,
        { steps: validSteps },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Steps updated successfully!");
        setShowEditModal(false);
        setEditingSteps({ id: null, steps: [] });
        fetchSteps();
      }
    } catch (error) {
      console.error("Error updating steps:", error);
      toast.error(error.response?.data?.message || "Failed to update steps");
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete steps
  const handleDeleteSteps = async () => {
    if (!selectedStep) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API_URL}/deletesteps/${selectedStep._id}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Steps deleted successfully!");
        setShowDeleteModal(false);
        setSelectedStep(null);
        fetchSteps();
      }
    } catch (error) {
      console.error("Error deleting steps:", error);
      toast.error(error.response?.data?.message || "Failed to delete steps");
    } finally {
      setDeleting(false);
    }
  };

  // Add new step field in create form
  const addStepField = () => {
    setNewSteps([...newSteps, { text: "", description: "" }]);
  };

  // Remove step field from create form
  const removeStepField = (index) => {
    if (newSteps.length > 1) {
      const updatedSteps = newSteps.filter((_, i) => i !== index);
      setNewSteps(updatedSteps);
    }
  };

  // Update step field in create form
  const updateStepField = (index, field, value) => {
    const updatedSteps = [...newSteps];
    updatedSteps[index][field] = value;
    setNewSteps(updatedSteps);
  };

  // Add step field in edit form
  const addEditStepField = () => {
    setEditingSteps({
      ...editingSteps,
      steps: [...editingSteps.steps, { text: "", description: "" }]
    });
  };

  // Remove step field from edit form
  const removeEditStepField = (index) => {
    if (editingSteps.steps.length > 1) {
      const updatedSteps = editingSteps.steps.filter((_, i) => i !== index);
      setEditingSteps({ ...editingSteps, steps: updatedSteps });
    }
  };

  // Update step field in edit form
  const updateEditStepField = (index, field, value) => {
    const updatedSteps = [...editingSteps.steps];
    updatedSteps[index][field] = value;
    setEditingSteps({ ...editingSteps, steps: updatedSteps });
  };

  // Open edit modal with steps data
  const openEditModal = (stepDoc) => {
    setEditingSteps({
      id: stepDoc._id,
      steps: stepDoc.steps.map(step => ({
        text: step.text,
        description: step.description || ""
      }))
    });
    setShowEditModal(true);
  };

  // Move step up in list
  const moveStepUp = (index) => {
    if (index === 0) return;
    const updatedSteps = [...editingSteps.steps];
    [updatedSteps[index - 1], updatedSteps[index]] = [updatedSteps[index], updatedSteps[index - 1]];
    setEditingSteps({ ...editingSteps, steps: updatedSteps });
  };

  // Move step down in list
  const moveStepDown = (index) => {
    if (index === editingSteps.steps.length - 1) return;
    const updatedSteps = [...editingSteps.steps];
    [updatedSteps[index], updatedSteps[index + 1]] = [updatedSteps[index + 1], updatedSteps[index]];
    setEditingSteps({ ...editingSteps, steps: updatedSteps });
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FiLoader className="animate-spin text-4xl text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
              <FiList className="text-blue-500" />
              Steps Management
            </h1>
            <p className="text-gray-600 mt-2 text-sm md:text-base">
              Total {filteredSteps.length} step sets found
            </p>
          </div>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
          >
            <FiPlus />
            Create New Steps
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search steps by title or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Steps Table */}
        {currentItems.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FiList className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Steps Found</h3>
            <p className="text-gray-500 mb-6">Get started by creating your first steps</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all"
            >
              <FiPlus />
              Create New Steps
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-500 to-purple-600">
                      <th className="py-4 px-6 text-left text-xs font-semibold text-white uppercase tracking-wider">
                        S.No
                      </th>
                      <th className="py-4 px-6 text-left text-xs font-semibold text-white uppercase tracking-wider">
                        Steps Details
                      </th>
                      <th className="py-4 px-6 text-left text-xs font-semibold text-white uppercase tracking-wider">
                        Total Steps
                      </th>
                      <th className="py-4 px-6 text-left text-xs font-semibold text-white uppercase tracking-wider">
                        Created At
                      </th>
                      <th className="py-4 px-6 text-left text-xs font-semibold text-white uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentItems.map((stepDoc, index) => (
                      <tr key={stepDoc._id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {indexOfFirstItem + index + 1}
                        </td>
                        <td className="py-4 px-6">
                          <div className="space-y-2">
                            {stepDoc.steps.map((step, idx) => (
                              <div key={idx} className="bg-blue-50 p-2 rounded-lg">
                                <div className="text-sm font-semibold text-gray-900">
                                  Step {idx + 1}: {step.text}
                                </div>
                                {step.description && (
                                  <div className="text-xs text-gray-600 mt-1">
                                    {step.description}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                            {stepDoc.steps.length} steps
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <div className="text-sm text-gray-600">
                            {formatDate(stepDoc.createdAt)}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openEditModal(stepDoc)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition border border-green-200"
                              title="Edit Steps"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedStep(stepDoc);
                                setShowDeleteModal(true);
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition border border-red-200"
                              title="Delete Steps"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-semibold">{indexOfFirstItem + 1}</span> to{" "}
                  <span className="font-semibold">{Math.min(indexOfLastItem, filteredSteps.length)}</span> of{" "}
                  <span className="font-semibold">{filteredSteps.length}</span> steps
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft />
                  </button>
                  
                  <span className="px-4 py-2 bg-blue-500 text-white rounded-lg">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Create Steps Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <FiPlus className="text-blue-500" />
                    Create New Steps
                  </h2>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <FiX className="text-xl" />
                  </button>
                </div>

                <form onSubmit={handleCreateSteps} className="space-y-4">
                  {newSteps.map((step, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg relative">
                      {newSteps.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeStepField(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <FiX className="text-sm" />
                        </button>
                      )}
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Step {index + 1} Title *
                          </label>
                          <input
                            type="text"
                            value={step.text}
                            onChange={(e) => updateStepField(index, "text", e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., Download App"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description (Optional)
                          </label>
                          <textarea
                            value={step.description}
                            onChange={(e) => updateStepField(index, "description", e.target.value)}
                            rows="2"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter step description"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addStepField}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-500 transition-all flex items-center justify-center gap-2"
                  >
                    <FiPlus />
                    Add Another Step
                  </button>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={creating}
                      className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {creating ? <FiLoader className="animate-spin" /> : <FiSave />}
                      {creating ? "Creating..." : "Create Steps"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Steps Modal */}
        {showEditModal && editingSteps.id && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <FiEdit2 className="text-green-500" />
                    Edit Steps
                  </h2>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingSteps({ id: null, steps: [] });
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <FiX className="text-xl" />
                  </button>
                </div>

                <form onSubmit={handleUpdateSteps} className="space-y-4">
                  {editingSteps.steps.map((step, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg relative">
                      <div className="absolute left-2 top-1/2 transform -translate-y-1/2 flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => moveStepUp(index)}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-blue-500 disabled:opacity-30"
                        >
                          <FiArrowUp />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveStepDown(index)}
                          disabled={index === editingSteps.steps.length - 1}
                          className="p-1 text-gray-400 hover:text-blue-500 disabled:opacity-30"
                        >
                          <FiArrowDown />
                        </button>
                      </div>

                      {editingSteps.steps.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEditStepField(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <FiX className="text-sm" />
                        </button>
                      )}
                      
                      <div className="ml-12 space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Step {index + 1} Title *
                          </label>
                          <input
                            type="text"
                            value={step.text}
                            onChange={(e) => updateEditStepField(index, "text", e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., Download App"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description (Optional)
                          </label>
                          <textarea
                            value={step.description}
                            onChange={(e) => updateEditStepField(index, "description", e.target.value)}
                            rows="2"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter step description"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addEditStepField}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-500 transition-all flex items-center justify-center gap-2"
                  >
                    <FiPlus />
                    Add Another Step
                  </button>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditModal(false);
                        setEditingSteps({ id: null, steps: [] });
                      }}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {updating ? <FiLoader className="animate-spin" /> : <FiSave />}
                      {updating ? "Updating..." : "Update Steps"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedStep && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="text-center">
                <FiTrash2 className="text-5xl text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Delete Steps</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete this steps set? This action cannot be undone.
                </p>
                
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteSteps}
                    disabled={deleting}
                    className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
                  >
                    {deleting ? <FiLoader className="animate-spin" /> : <FiTrash2 />}
                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StepsManagement;